import { FC } from "react";
import { Casino } from "@mui/icons-material";
import { hexToString, toBytes } from "viem";

import type { IServerSignature } from "@dzambalaorg/types-blockchain";
import {
  useAllowance,
  useMetamask,
  useServerSignature,
  useWriteContractWithSimulation,
} from "@dzambalaorg/react-hooks-eth";
import {
  convertDatabaseAssetToChainAsset,
  convertDatabaseAssetToTokenTypeAsset,
  getEthPrice,
} from "@framework/exchange";
import { ListAction, ListActionVariant } from "@framework/styled";
import { AddressType, IContract, IRaffleRound } from "@framework/types";
import RafflePurchaseABI from "@framework/abis/json/ExchangeRaffleFacet/purchaseRaffle.json";
import { ZeroAddress } from "@framework/constants";

export interface IRafflePurchaseButtonProps {
  className?: string;
  disabled?: boolean;
  round: Partial<IRaffleRound>;
  variant?: ListActionVariant;
}

export const RafflePurchaseButton: FC<IRafflePurchaseButtonProps> = props => {
  const { className, disabled, round, variant = ListActionVariant.button } = props;

  const referrer = ZeroAddress;

  const { writeContractAsync } = useWriteContractWithSimulation();

  const { execute: metaFnWithAllowance } = useAllowance(async (sign: IServerSignature, systemContract: IContract) => {
    const price = convertDatabaseAssetToChainAsset(round.price?.components);

    return writeContractAsync({
      address: systemContract.address as AddressType,
      abi: RafflePurchaseABI,
      functionName: "purchaseRaffle",
      args: [
        {
          externalId: round.id,
          expiresAt: sign.expiresAt,
          nonce: toBytes(sign.nonce, { size: 32 }),
          extra: hexToString("0x", { size: 32 }),
          receiver: round.contract?.address,
          referrer,
        },
        {
          tokenType: 2,
          token: round.ticketContract?.address,
          tokenId: "0",
          amount: "1",
        },
        price[0],
        sign.signature,
        {
          value: getEthPrice(round.price),
        },
      ],
    });
  });

  const metaFnWithSign = useServerSignature((_values: null, sign: IServerSignature, systemContract: IContract) => {
    const price = convertDatabaseAssetToTokenTypeAsset(round.price?.components);
    return metaFnWithAllowance(
      {
        contract: systemContract.address,
        assets: price,
      },
      sign,
      systemContract,
    );
  });

  const { execute: metaFn } = useMetamask(() => {
    return metaFnWithSign(
      {
        url: "/raffle/ticket/sign",
        method: "POST",
        data: {
          referrer,
          contractId: round.contractId,
        },
      },
      null,
    ) as Promise<void>;
  });

  const handlePurchase = () => {
    return metaFn();
  };

  return (
    <ListAction
      onClick={handlePurchase}
      icon={Casino}
      message="form.buttons.buy"
      buttonVariant="primary"
      className={className}
      dataTestId="RafflePurchaseButton"
      disabled={
        disabled ||
        !round?.roundId ||
        (!!round.maxTickets && round.maxTickets > 0 && round.maxTickets <= round.ticketCount!)
      }
      variant={variant}
    />
  );
};
